import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, Languages, Upload, X, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';

import { API, API_URL } from '@/config';

const LessonModal = ({ open, onClose, lesson, token, onSuccess, i18n, t }) => {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'traffic_signs',
    source_language: 'ar',
    image_url: '',
    order: 0
  });

  useEffect(() => {
    if (lesson) {
      const imageUrl = lesson.image_url || '';
      setFormData({
        title: lesson.title?.[i18n.language] || lesson.title?.en || '',
        content: lesson.content?.[i18n.language] || lesson.content?.en || '',
        category: lesson.category || 'traffic_signs',
        source_language: i18n.language,
        image_url: imageUrl,
        order: lesson.order || 0
      });
      setPreviewImage(imageUrl);
    } else {
      setFormData({
        title: '',
        content: '',
        category: 'traffic_signs',
        source_language: i18n.language,
        image_url: '',
        order: 0
      });
      setPreviewImage('');
    }
  }, [lesson, open, i18n.language]);

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error(i18n.language === 'ar' 
        ? 'صيغة الملف غير مدعومة. استخدم: JPG, PNG, GIF, WEBP'
        : 'Invalid file type. Use: JPG, PNG, GIF, WEBP'
      );
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error(i18n.language === 'ar' 
        ? 'حجم الملف يجب أن يكون أقل من 10MB'
        : 'File size must be less than 10MB'
      );
      return;
    }

    setUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await axios.post(
        `${API_URL}/api/upload/image`,
        uploadFormData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        // Check if URL is already absolute (from Cloudinary)
        const imageUrl = response.data.url.startsWith('http') 
          ? response.data.url 
          : `${API_URL}${response.data.url}`;
        setFormData({ ...formData, image_url: imageUrl });
        setPreviewImage(imageUrl);
        toast.success(i18n.language === 'ar' ? 'تم رفع الصورة! ✅' : 'Image uploaded! ✅');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(i18n.language === 'ar' ? 'خطأ في رفع الصورة' : 'Error uploading image');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setFormData({ ...formData, image_url: '' });
    setPreviewImage('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error(i18n.language === 'ar' ? 'يرجى ملء جميع الحقول المطلوبة' : 'Please fill all required fields');
      return;
    }

    setLoading(true);
    
    try {
      const endpoint = lesson 
        ? `${API}/admin/lessons/${lesson.id}/auto-translate`
        : `${API}/admin/lessons/auto-translate`;
      
      const method = lesson ? 'put' : 'post';
      
      const response = await axios[method](endpoint, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success(
          lesson 
            ? (i18n.language === 'ar' ? 'تم تحديث الدرس وترجمته! 🎉' : 'Lesson updated and translated! 🎉')
            : (i18n.language === 'ar' ? 'تم إضافة الدرس وترجمته! 🎉' : 'Lesson added and translated! 🎉')
        );
        onSuccess();
        onClose();
      }
    } catch (error) {
      console.error('Error saving lesson:', error);
      toast.error(i18n.language === 'ar' ? 'خطأ في حفظ الدرس' : 'Error saving lesson');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Languages className="w-6 h-6 text-emerald-600" />
            {lesson 
              ? (i18n.language === 'ar' ? 'تعديل الدرس' : 'Edit Lesson')
              : (i18n.language === 'ar' ? 'إضافة درس جديد' : 'Add New Lesson')
            }
          </DialogTitle>
          <p className="text-sm text-slate-500 mt-2">
            {i18n.language === 'ar' 
              ? '✨ سيتم ترجمة الدرس تلقائياً إلى جميع اللغات'
              : '✨ Lesson will be automatically translated to all languages'
            }
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <Label className="text-sm font-semibold text-amber-900 mb-2 block">
              {i18n.language === 'ar' ? 'لغة الإدخال' : 'Input Language'}
            </Label>
            <Select value={formData.source_language} onValueChange={(val) => setFormData({ ...formData, source_language: val })}>
              <SelectTrigger className="bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sv">🇸🇪 Svenska</SelectItem>
                <SelectItem value="en">🇬🇧 English</SelectItem>
                <SelectItem value="ar">🇸🇦 العربية</SelectItem>
                <SelectItem value="tr">🇹🇷 Türkçe</SelectItem>
                <SelectItem value="fa">🇮🇷 فارسی</SelectItem>
                <SelectItem value="so">🇸🇴 Soomaali</SelectItem>
                <SelectItem value="pl">🇵🇱 Polski</SelectItem>
                <SelectItem value="es">🇪🇸 Español</SelectItem>
                <SelectItem value="de">🇩🇪 Deutsch</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-sm font-semibold">
              {i18n.language === 'ar' ? 'العنوان *' : 'Title *'}
            </Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder={i18n.language === 'ar' ? 'عنوان الدرس' : 'Lesson title'}
              className="mt-2"
              required
            />
          </div>

          <div>
            <Label className="text-sm font-semibold">
              {i18n.language === 'ar' ? 'المحتوى *' : 'Content *'}
            </Label>
            <Textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              placeholder={i18n.language === 'ar' ? 'محتوى الدرس' : 'Lesson content'}
              className="mt-2"
              rows={6}
              required
            />
          </div>

          <div>
            <Label className="text-sm font-semibold">
              {i18n.language === 'ar' ? 'الفئة' : 'Category'}
            </Label>
            <Select value={formData.category} onValueChange={(val) => setFormData({ ...formData, category: val })}>
              <SelectTrigger className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="traffic_signs">{i18n.language === 'ar' ? 'إشارات المرور' : 'Traffic Signs'}</SelectItem>
                <SelectItem value="road_rules">{i18n.language === 'ar' ? 'قواعد الطريق' : 'Road Rules'}</SelectItem>
                <SelectItem value="safety">{i18n.language === 'ar' ? 'السلامة' : 'Safety'}</SelectItem>
                <SelectItem value="parking">{i18n.language === 'ar' ? 'الوقوف' : 'Parking'}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-semibold">
              {i18n.language === 'ar' ? 'صورة الدرس (اختياري)' : 'Lesson Image (Optional)'}
            </Label>
            
            {/* Upload Button */}
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-2"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {i18n.language === 'ar' ? 'جاري الرفع...' : 'Uploading...'}
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    {i18n.language === 'ar' ? 'رفع صورة من حاسوبك' : 'Upload from Computer'}
                  </>
                )}
              </Button>
              
              {previewImage && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleRemoveImage}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <X className="w-4 h-4 me-1" />
                  {i18n.language === 'ar' ? 'إزالة' : 'Remove'}
                </Button>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              onChange={handleFileUpload}
              className="hidden"
            />

            {/* Image Preview */}
            {previewImage && (
              <div className="mt-3 border-2 border-dashed border-slate-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <ImageIcon className="w-5 h-5 text-emerald-600" />
                  <span className="text-sm text-slate-600">
                    {i18n.language === 'ar' ? 'معاينة الصورة:' : 'Image Preview:'}
                  </span>
                </div>
                <img 
                  src={previewImage} 
                  alt="Preview" 
                  className="mt-3 max-w-full h-auto max-h-48 rounded-lg object-cover"
                />
              </div>
            )}

            {/* OR Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-2 bg-white text-slate-500">
                  {i18n.language === 'ar' ? 'أو' : 'OR'}
                </span>
              </div>
            </div>

            {/* URL Input */}
            <Input
              type="text"
              value={formData.image_url}
              onChange={(e) => {
                setFormData({ ...formData, image_url: e.target.value });
                setPreviewImage(e.target.value);
              }}
              placeholder={i18n.language === 'ar' ? 'أو الصق رابط الصورة هنا...' : 'Or paste image URL here...'}
              dir="ltr"
            />
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              {i18n.language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button type="submit" disabled={loading} className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600">
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {i18n.language === 'ar' ? 'جاري الترجمة...' : 'Translating...'}
                </>
              ) : (
                <>
                  <Languages className="w-4 h-4 mr-2" />
                  {lesson 
                    ? (i18n.language === 'ar' ? 'تحديث وترجمة' : 'Update & Translate')
                    : (i18n.language === 'ar' ? 'إضافة وترجمة' : 'Add & Translate')
                  }
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LessonModal;
